import { Download } from "lucide-react";
import { toast } from "react-toastify";
import { bold, exportXlsx } from "../../utils/xlsx";

export const REPORTS = "/api/v1/accounting/reports";
export const ACCOUNTING = "/api/v1/accounting";

export const money = (value) =>
  value === null || value === undefined || value === ""
    ? "-"
    : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Ratios / percentages can be null when the denominator is zero. */
export const ratio = (value, suffix = "") => (value === null || value === undefined ? "N/A" : `${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}${suffix}`);

const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const todayIso = () => iso(new Date());

/** Indian financial year: 1 April – 31 March. */
export const fyStartIso = (offsetYears = 0) => {
  const now = new Date();
  const startYear = (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1) + offsetYears;
  return `${startYear}-04-01`;
};

const PRESETS = [
  { key: "thisFy", label: "This financial year" },
  { key: "lastFy", label: "Last financial year" },
  { key: "thisQuarter", label: "This quarter" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "today", label: "Today" },
  { key: "custom", label: "Custom dates" },
];

export const presetRange = (key) => {
  const now = new Date();
  switch (key) {
    case "lastFy": {
      const start = fyStartIso(-1);
      return { fromDate: start, toDate: `${Number(start.slice(0, 4)) + 1}-03-31` };
    }
    case "thisQuarter":
      return { fromDate: iso(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)), toDate: todayIso() };
    case "thisMonth":
      return { fromDate: iso(new Date(now.getFullYear(), now.getMonth(), 1)), toDate: todayIso() };
    case "lastMonth":
      return { fromDate: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), toDate: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
    case "today":
      return { fromDate: todayIso(), toDate: todayIso() };
    default:
      return { fromDate: fyStartIso(), toDate: todayIso() };
  }
};

export const defaultPeriod = () => ({ preset: "thisFy", ...presetRange("thisFy") });

/** Period filter: quick presets plus from / to dates. `period` = { preset, fromDate, toDate }. */
export const PeriodFilter = ({ period, onChange }) => (
  <>
    <select
      value={period.preset}
      aria-label="Period"
      onChange={(e) => onChange(e.target.value === "custom" ? { ...period, preset: "custom" } : { preset: e.target.value, ...presetRange(e.target.value) })}
    >
      {PRESETS.map((preset) => (
        <option key={preset.key} value={preset.key}>
          {preset.label}
        </option>
      ))}
    </select>
    <input
      type="date"
      onClick={(e) => e.target.showPicker?.()}
      value={period.fromDate}
      onChange={(e) => onChange({ ...period, preset: "custom", fromDate: e.target.value })}
      aria-label="From date"
    />
    <input
      type="date"
      onClick={(e) => e.target.showPicker?.()}
      value={period.toDate}
      onChange={(e) => onChange({ ...period, preset: "custom", toDate: e.target.value })}
      aria-label="To date"
    />
  </>
);

export const AsOfFilter = ({ value, onChange }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--color-text-muted)" }}>
    As on
    <input type="date" onClick={(e) => e.target.showPicker?.()} value={value} onChange={(e) => onChange(e.target.value)} aria-label="As on date" />
  </label>
);

export const validPeriod = (period) => {
  if (!period.fromDate || !period.toDate) {
    toast.error("Select both from and to dates");
    return false;
  }
  if (period.fromDate > period.toDate) {
    toast.error("From date must be on or before to date");
    return false;
  }
  return true;
};

export const LoadingOverlay = ({ show, label = "Loading report..." }) =>
  show ? (
    <div className="loading-overlay">
      <div className="spinner-card">
        <div className="spinner-ring" />
        <div className="spinner-label">{label}</div>
      </div>
    </div>
  ) : null;

export const ExportButton = ({ onClick, disabled, label = "Export Excel" }) => (
  <button type="button" className="secondary" onClick={onClick} disabled={disabled} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
    <Download size={15} />
    {label}
  </button>
);

export const EmptyState = ({ children }) => (
  <div className="empty-state">
    <span className="empty-state-title">{children}</span>
  </div>
);

export const StatCard = ({ label, value, hint, tone }) => (
  <div className="stat-card">
    <div className="stat-card-label">{label}</div>
    <div
      className="stat-card-value"
      style={tone ? { color: tone === "good" ? "var(--color-success)" : tone === "bad" ? "var(--color-danger)" : undefined } : undefined}
    >
      {value}
    </div>
    {hint ? <div className="page-subtitle" style={{ marginTop: "4px" }}>{hint}</div> : null}
  </div>
);

export const signTone = (value) => (value === null || value === undefined ? undefined : Number(value) >= 0 ? "good" : "bad");

/** Title rows at the top of every exported sheet. */
export const sheetHeading = (title, subtitle) => [[bold(title)], [subtitle || ""], []];

export const periodLabel = (period) => `${period.fromDate} to ${period.toDate}`;

export const download = (name, sheets) => {
  try {
    exportXlsx(name, sheets);
  } catch (error) {
    toast.error(error.message || "Export failed");
  }
};

export const cellRight = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
